import { h, Fragment } from 'preact';

import { route } from 'preact-router';
import { useDispatch, useSelector } from 'react-redux';

import { PUBLIC_FOLDER } from '../consts.js';
import { getLruCache, v1Endpoints, v2Endpoints } from '../apis/kappa.js';
import { actions as endpointActions } from '../store/state/endpoints.js';
import { actions as selectedEndpointActions } from '../store/state/selectedEndpoint.js';
import { loadAllEndpoints } from '../utils/endpointUtils.js';
import { CODE_AREA_LABELS } from '../utils/editorUrils.js';
import { defineMethod } from './world.js';
import { useEditorSession, useObjectRegistration } from './hooks.jsx';
import { getStepOutput, listStepOutputs } from './runOutputs.js';

const VERSIONS = ['v1', 'v2'];
const APIS = { v1: v1Endpoints, v2: v2Endpoints };

function requireVersion(input) {
  if (!VERSIONS.includes(input.version)) {
    throw new Error('version must be "v1" or "v2"');
  }
  return input.version;
}

function toEndpointSummary(endpoint) {
  return {
    id: endpoint._id,
    name: endpoint.name,
    displayName: endpoint.displayName || null,
    method: endpoint.method || null,
  };
}

function EndpointDirectoryRegistration(_props) {
  const dispatch = useDispatch();
  const endpoints = useSelector(state => state.endpoints);

  useObjectRegistration({
    id: 'endpoint-directory',
    type: 'EndpointDirectory',
    props: {
      v1Count: endpoints.list.v1.length,
      v2Count: endpoints.list.v2.length,
      loading: endpoints.loading,
      v1LoadError: endpoints.errors.v1,
      v2LoadError: endpoints.errors.v2,
    },
    methods: {
      listEndpoints: defineMethod({
        description:
          'List the loaded custom endpoints of one runtime version (v2 is the current runtime, v1 is legacy) with their ids, path slugs, display names, and HTTP methods.',
        parameters: {
          type: 'object',
          properties: {
            version: { type: 'string', enum: VERSIONS },
            offset: { type: 'integer', minimum: 0 },
            limit: { type: 'integer', minimum: 1, maximum: 50 },
          },
          required: ['version'],
        },
        method: input => {
          const version = requireVersion(input);
          const offset = input.offset || 0;
          const limit = input.limit || 25;
          const list = endpoints.list[version];
          const loadError = endpoints.errors[version];
          return {
            version,
            total: list.length,
            offset,
            endpoints: list.slice(offset, offset + limit).map(toEndpointSummary),
            // Surfaced so the agent reports the real failure (e.g. the user is
            // not signed in) instead of guessing at an empty list.
            ...(loadError ? { loadError } : {}),
          };
        },
      }),
      openEndpoint: defineMethod({
        description: 'Open the editor of one endpoint by version and id.',
        parameters: {
          type: 'object',
          properties: {
            version: { type: 'string', enum: VERSIONS },
            id: { type: 'string', minLength: 1 },
          },
          required: ['version', 'id'],
        },
        method: input => {
          const version = requireVersion(input);
          const endpoint = endpoints.list[version].find(
            candidate => candidate._id === input.id
          );
          if (!endpoint) {
            throw new Error(
              `No loaded ${version} endpoint has the id "${input.id}". Use listEndpoints first.`
            );
          }
          route(`${PUBLIC_FOLDER}/${version}/edit/${input.id}`);
          return `Opened the editor of "${endpoint.displayName || endpoint.name}"`;
        },
      }),
      createEndpoint: defineMethod({
        description:
          'Open the form to create a new endpoint in the chosen runtime version.',
        parameters: {
          type: 'object',
          properties: { version: { type: 'string', enum: VERSIONS } },
          required: ['version'],
        },
        method: input => {
          const version = requireVersion(input);
          route(`${PUBLIC_FOLDER}/${version}`);
          return `Opened the new ${version} endpoint form`;
        },
      }),
      reloadEndpoints: defineMethod({
        description: 'Reload the endpoint list of one runtime version.',
        parameters: {
          type: 'object',
          properties: { version: { type: 'string', enum: VERSIONS } },
          required: ['version'],
        },
        method: async input => {
          const version = requireVersion(input);
          dispatch(endpointActions.SET_LIST({ version, list: [] }));
          await loadAllEndpoints(version);
          return `Reloaded the ${version} endpoints`;
        },
      }),
      inspectLruCache: defineMethod({
        description:
          'Read the current LRU storage of the client backends runtime (the persistent key-value cache scripts can use).',
        parameters: { type: 'object', properties: {} },
        method: () => getLruCache(),
      }),
    },
  });

  return null;
}

const DRAFT_PARAMETERS = {
  type: 'object',
  description:
    'The fields to put into the editor. Prefer `fromStep` ALONE: the definition is taken from the run step output the platform already sent to this page and applied verbatim; any other field passed next to it is ignored, so nothing is retyped. Without `fromStep` every field is optional; omitted fields keep their current value. Send the WHOLE script when it changes.',
  properties: {
    fromStep: {
      type: 'string',
      description:
        'Id of a workflow step of THIS run whose published output carries a `definition` (the Client Backends copilot writes it in step "generate"). Its fields are applied verbatim; every other field of this call is ignored (the result names them).',
    },
    displayName: { type: 'string' },
    name: { type: 'string', description: 'Path slug: letters, digits, ".", "_", "~", "-" only.' },
    method: { type: 'string', enum: ['POST', 'GET', 'DELETE', 'PUT', 'PATCH', 'OPTIONS', 'HEAD'] },
    script: { type: 'string', description: 'The whole script (async function main(input) …).' },
    schemas: {
      type: 'object',
      description: 'JTD schemas as JSON strings, merged per key.',
      properties: {
        query: { type: 'string' },
        body: { type: 'string' },
        cookies: { type: 'string' },
        headers: { type: 'string' },
        successResponse: { type: 'string' },
        errorResponse: { type: 'string' },
      },
      additionalProperties: false,
    },
    originWhitelist: { type: 'array', items: { type: 'string' } },
    enabled: { type: 'boolean' },
    internal: { type: 'boolean' },
    responseType: { type: 'string' },
    isLongRunning: { type: 'boolean' },
    allowPlatformWrites: { type: 'boolean' },
  },
  additionalProperties: false,
};

/**
 * The draft fields to apply, plus the names of the fields that were dropped.
 * With `fromStep` the referenced step's `definition` is taken verbatim and
 * every field passed alongside is ignored: a model that retypes the
 * definition next to the reference would otherwise put its own copy over
 * the validated one — the drift `fromStep` exists to rule out (seen on the
 * first production run of the by-reference applier, which sent both).
 */
export function resolveDraftInput(input) {
  const { fromStep, ...explicit } = input || {};
  if (!fromStep) {
    return { draft: explicit, ignored: [] };
  }
  const output = getStepOutput(fromStep);
  if (output === undefined) {
    const available = listStepOutputs();
    throw new Error(
      `No output of step "${fromStep}" was published for this run` +
        (available.length ? ` (available: ${available.join(', ')})` : ' (the step has not completed, or the workflow does not publish it)') +
        '. Pass the fields explicitly instead.'
    );
  }
  const definition = output && typeof output.definition === 'object' && output.definition ? output.definition : output;
  return { draft: { ...definition }, ignored: Object.keys(explicit) };
}

export function defineSetDraft(session, what) {
  return defineMethod({
    description: `Put a draft into ${what}: writes the given fields into the editor and the sidebar form WITHOUT saving, so the user can review the diff. Nothing is persisted until the user saves (or asks to save). Pass fromStep to take the definition from the run's own step output. Returns what changed.`,
    parameters: DRAFT_PARAMETERS,
    method: input => {
      const { draft, ignored } = resolveDraftInput(input);
      const result = session.applyDraft(draft);
      const applied = result.changes.length
        ? `Draft applied to the editor (unsaved): ${result.changes.join('; ')}`
        : 'Draft applied to the editor (unsaved); nothing differed from the current values';
      const shown = result.shownView
        ? ` The editor now shows the ${CODE_AREA_LABELS[result.shownView] || result.shownView} tab.`
        : '';
      return {
        ...result,
        source: input && input.fromStep ? `step ${input.fromStep}` : 'arguments',
        ignored,
        message:
          (ignored.length
            ? `${applied}. Ignored ${ignored.join(', ')}: with fromStep the step's definition is applied verbatim, nothing passed alongside it.`
            : applied) + shown,
      };
    },
  });
}

function readerUrl(name) {
  return `${window.location.origin}/r/kappa/api/v2/reader/${name}`;
}

function defineSave(session, what) {
  return defineMethod({
    description: `Save ${what} as it currently is — the same request the sidebar's Save button makes, as the signed-in user. Call it only after the user confirmed the save. Returns the endpoint id, name and URL; a validation refusal comes back as an error with the site's message.`,
    parameters: { type: 'object', properties: {} },
    method: async () => {
      if (typeof session.save !== 'function') {
        throw new Error('Saving is not available on this page');
      }
      const saved = await session.save();
      return {
        saved: true,
        id: saved.id,
        name: saved.name,
        url: readerUrl(saved.name),
        editorUrl: `${window.location.origin}${PUBLIC_FOLDER}/v2/edit/${saved.id}`,
      };
    },
  });
}

function NewEndpointRegistration(props) {
  const { session } = props;

  useObjectRegistration({
    id: 'endpoint:new',
    type: 'Endpoint',
    props: {
      endpointId: null,
      version: session.version,
      mode: 'new',
      dirty: true,
    },
    methods: {
      getDefinition: defineMethod({
        description:
          'Read the new endpoint form as the user currently has it: script, schemas, and settings, unsaved.',
        parameters: { type: 'object', properties: {} },
        method: () => session.getDefinition(),
      }),
      setDraft: defineSetDraft(session, 'the new endpoint form'),
      save: defineSave(session, 'the new endpoint form (creates the endpoint)'),
    },
  });

  return null;
}

function SelectedEndpointRegistration(props) {
  const { endpointId, model, version, session } = props;
  const dispatch = useDispatch();
  const api = APIS[version];
  // The editor's live (unsaved) state when the v2 editor is mounted; the
  // server copy otherwise (v1, or before the editor registered).
  const live = session && session.mode === 'edit' && session.endpointId === endpointId ? session : null;

  useObjectRegistration({
    id: `endpoint:${endpointId}`,
    type: 'Endpoint',
    props: {
      endpointId,
      version,
      name: model.name || null,
      displayName: model.displayName || null,
      method: model.method || null,
      dirty: live ? live.isDirty() : false,
    },
    methods: {
      getDefinition: defineMethod({
        description:
          'Read the full definition of the open endpoint: script, schemas, and settings, exactly as the editor currently holds them (unsaved edits included).',
        parameters: { type: 'object', properties: {} },
        method: () => (live ? live.getDefinition() : model),
      }),
      ...(live
        ? {
            setDraft: defineSetDraft(live, 'the open endpoint\'s editor'),
            save: defineSave(live, 'the open endpoint\'s editor (updates the endpoint)'),
          }
        : {}),
      refresh: defineMethod({
        description: 'Reload the open endpoint from the server, discarding unsaved editor changes.',
        parameters: { type: 'object', properties: {} },
        method: () => {
          dispatch(selectedEndpointActions.TRIGGER_REFRESH());
          return 'Reloaded the endpoint';
        },
      }),
      listVersions: defineMethod({
        description: 'List the saved change history of the open endpoint.',
        parameters: {
          type: 'object',
          properties: {
            offset: { type: 'integer', minimum: 0 },
            limit: { type: 'integer', minimum: 1, maximum: 50 },
          },
        },
        method: input => api.listVersions(endpointId, input.limit || 20, input.offset || 0),
      }),
      getChange: defineMethod({
        description: 'Read one saved change of the open endpoint by change id.',
        parameters: {
          type: 'object',
          properties: { changeId: { type: 'string', minLength: 1 } },
          required: ['changeId'],
        },
        method: input => {
          if (!input.changeId) {
            throw new Error('changeId is required');
          }
          return api.getChange(endpointId, input.changeId);
        },
      }),
      listLogs: defineMethod({
        description: 'Read the recent execution logs of the open endpoint.',
        parameters: {
          type: 'object',
          properties: {
            offset: { type: 'integer', minimum: 0 },
            limit: { type: 'integer', minimum: 1, maximum: 50 },
          },
        },
        method: input => api.listLogs(endpointId, input.limit || 20, input.offset || 0),
      }),
      rollbackToChange: defineMethod({
        description:
          'Restore the open endpoint to one of its saved changes. This replaces the current saved version.',
        parameters: {
          type: 'object',
          properties: { changeId: { type: 'string', minLength: 1 } },
          required: ['changeId'],
        },
        mutates: true,
        method: async input => {
          if (!input.changeId) {
            throw new Error('changeId is required');
          }
          await api.rollbackToChange(endpointId, input.changeId);
          dispatch(selectedEndpointActions.TRIGGER_REFRESH());
          return `Restored change ${input.changeId}`;
        },
      }),
    },
  });

  return null;
}

export default function CopilotObjects(_props) {
  const selectedEndpoint = useSelector(state => state.selectedEndpoint);
  const session = useEditorSession();
  const version =
    typeof window !== 'undefined' && window.location.pathname.includes('/v1/')
      ? 'v1'
      : 'v2';

  return (
    <Fragment>
      <EndpointDirectoryRegistration />
      {selectedEndpoint.endpointId && selectedEndpoint.model && (
        <SelectedEndpointRegistration
          endpointId={selectedEndpoint.endpointId}
          model={selectedEndpoint.model}
          version={version}
          session={session}
        />
      )}
      {session && session.mode === 'new' && <NewEndpointRegistration session={session} />}
    </Fragment>
  );
}
